import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { useTranslation } from 'react-i18next';

import Dropdown from '../../../component/input/Dropdown';
import MultiSelect from '../../../component/input/MultiSelect';
import CalendarInput from '../../../component/input/CalendarInput';
import InputSwitch from '../../../component/input/InputSwitch';

// @ts-ignore
import { PlanogramPendingService } from '../../../service/PlanogramPendingService';

// @ts-ignore
import { ReactComponent as QuestionMarkIcon } from '../../../assets/images/questionmark-icon.svg';
// @ts-ignore
import { ReactComponent as WarningTriangleIcon } from '../../../assets/images/warning-triangle-icon.svg';

import { getNextBusinessDay } from '../../../utils/date/DateUtils';

// @ts-ignore
import './CopyPlanogramToDeviceDialog.scss';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DeviceOption {
    id: string;
    label: string;
    uuid?: string | null;
    externalId?: string | null;
    [key: string]: unknown;
}

interface LocationOption {
    id: string;
    label: string;
    uuid?: string | null;
    externalId?: string | null;
    [key: string]: unknown;
}

interface FormData {
    device: DeviceOption[];
    location: LocationOption | null;
    applyImmediately: boolean;
    clearCells: boolean;
}

interface DefaultValues {
    device?: DeviceOption[];
    location?: LocationOption | null;
    applyImmediately?: boolean;
    clearCells?: boolean;
}

interface CopyDialogPlanogramData {
    id?: string | number | null;
    uuid?: string | number | null;
    externalId?: string | number | null;
    name?: string | null;
    rows?: number | null;
    columns?: number | null;
    cells?: unknown;
    status?: string | null;
    systemId?: string | number | null;
    externalSystemId?: string | number | null;
    tiedDevicesCount?: number | null;
    lockResult?: unknown;
    [key: string]: unknown;
}

interface PendingDevice {
    id?: string | number | null;
    uuid?: string | number | null;
    externalId?: string | number | null;
    name?: string | null;
    [key: string]: unknown;
}

interface PendingPlanogramRow {
    id?: string | number | null;
    uuid?: string | number | null;
    externalId?: string | number | null;
    name?: string | null;
    device?: PendingDevice | null;
    location?: unknown;
    oldPlanogramPendings?: unknown;
    implementationDate?: string | null;
    status?: string | null;
    createdAt?: string | null;
    [key: string]: unknown;
}

export interface SavePayload {
    device: DeviceOption[];
    location: LocationOption | null;
    applyImmediately: boolean;
    clearCells: boolean;
    allDevicesAtLocation: boolean;
    implementationDate: string;
}

export interface CopyPlanogramToDeviceDialogProps {
    visible: boolean;
    onHide: () => void;
    onSave: (payload: SavePayload) => Promise<unknown> | unknown;
    onComplete?: (processedDeviceCount: number) => void;
    defaultValues?: DefaultValues | null;
    planogramTitle?: string | null;
    planogramRows?: number | null;
    planogramColumns?: number | null;
    planogramData?: CopyDialogPlanogramData | null;
    deviceExternalIdToExclude?: string | null;
    planogramExternalIdToExclude?: string | null;
    isPlanogramComplete?: (() => boolean) | null;
    title?: string | null;
    incompleteTitle?: string | null;
    transformPlanogram?: boolean;
}

const getValueAsString = (value: unknown): string | null => {
    if (value === null || value === undefined) return null;

    const stringValue = String(value).trim();

    return stringValue || null;
};

const getArrayFromResponse = <T,>(response: unknown): T[] => {
    if (Array.isArray(response)) {
        return response as T[];
    }

    if (response && typeof response === 'object') {
        const objectResponse = response as Record<string, unknown>;

        if (Array.isArray(objectResponse.content)) {
            return objectResponse.content as T[];
        }

        if (Array.isArray(objectResponse.data)) {
            return objectResponse.data as T[];
        }

        if (Array.isArray(objectResponse.items)) {
            return objectResponse.items as T[];
        }

        if (Array.isArray(objectResponse.elements)) {
            return objectResponse.elements as T[];
        }

        if (
            objectResponse.data &&
            typeof objectResponse.data === 'object'
        ) {
            return getArrayFromResponse<T>(objectResponse.data);
        }
    }

    return [];
};

const getOptionId = (item: Record<string, unknown>): string => {
    return String(
        item.uuid ??
        item.externalId ??
        item.external_id ??
        item.id ??
        '',
    );
};

const getOptionLabel = (
    item: Record<string, unknown>,
    fallbackId: string,
): string => {
    return String(
        item.name ??
        item.deviceName ??
        item.locationName ??
        item.label ??
        fallbackId,
    );
};

const mapDeviceToOption = (
    device: Record<string, unknown>,
): DeviceOption => {
    const id = getOptionId(device);

    return {
        ...device,
        id,
        uuid: getValueAsString(device.uuid),
        externalId:
            getValueAsString(device.externalId) ??
            getValueAsString(device.external_id) ??
            getValueAsString(device.uuid) ??
            getValueAsString(id),
        label: getOptionLabel(device, id),
    };
};

const mapLocationToOption = (
    location: Record<string, unknown>,
): LocationOption => {
    const id = getOptionId(location);

    return {
        ...location,
        id,
        uuid: getValueAsString(location.uuid),
        externalId:
            getValueAsString(location.externalId) ??
            getValueAsString(location.external_id) ??
            getValueAsString(location.uuid) ??
            getValueAsString(id),
        label: getOptionLabel(location, id),
    };
};

const getDeviceOptionKey = (
    device?: DeviceOption | null,
): string | null =>
    getValueAsString(device?.id) ??
    getValueAsString(device?.uuid) ??
    getValueAsString(device?.externalId);

const isSameDeviceOption = (
    first?: DeviceOption | null,
    second?: DeviceOption | null,
): boolean => {
    const firstKey = getDeviceOptionKey(first);
    const secondKey = getDeviceOptionKey(second);

    return Boolean(
        firstKey &&
        secondKey &&
        firstKey === secondKey,
    );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const CopyPlanogramToDeviceDialog: React.FC<CopyPlanogramToDeviceDialogProps> = ({
    visible,
    onHide,
    onSave,
    onComplete,
    defaultValues,
    planogramTitle,
    planogramRows,
    planogramColumns,
    planogramData,
    deviceExternalIdToExclude,
    planogramExternalIdToExclude,
    isPlanogramComplete,
    title,
    incompleteTitle,
}) => {
    const { t } = useTranslation();
    const saveButtonRef = useRef<Button | null>(null);

    const [isSaving, setIsSaving] = useState(false);
    const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
    const [effectiveDate, setEffectiveDate] = useState<Date | null>(null);
    const [showIncompleteWarning, setShowIncompleteWarning] = useState(false);

    const [isLoadingLocations, setIsLoadingLocations] = useState(false);
    const [isLoadingDevices, setIsLoadingDevices] = useState(false);
    const [internalLocationOptions, setInternalLocationOptions] = useState<LocationOption[]>([]);
    const [internalDeviceOptions, setInternalDeviceOptions] = useState<DeviceOption[]>([]);

    const locationsRequestIdRef = useRef(0);
    const devicesRequestIdRef = useRef(0);

    const [formData, setFormData] = useState<FormData>({
        device: defaultValues?.device ?? [],
        location: defaultValues?.location ?? null,
        applyImmediately: defaultValues?.applyImmediately ?? false,
        clearCells: defaultValues?.clearCells ?? false,
    });

    const resolvedRows = planogramRows ?? planogramData?.rows ?? null;
    const resolvedColumns = planogramColumns ?? planogramData?.columns ?? null;

    // -----------------------------------------------------------------------
    // Basic helpers
    // -----------------------------------------------------------------------


    const normalizeText = (value: unknown): string => {
        return String(value ?? '')
            .trim()
            .toLowerCase();
    };

    const formatLocalDateKey = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    };

    const getDateKey = (value: unknown): string | null => {
        if (!value) return null;

        if (value instanceof Date) {
            return formatLocalDateKey(value);
        }

        if (typeof value === 'string') {
            if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                return value;
            }

            const parsedDate = new Date(value);

            if (!Number.isNaN(parsedDate.getTime())) {
                return formatLocalDateKey(parsedDate);
            }
        }

        return null;
    };

    const sleep = (milliseconds: number): Promise<void> => {
        return new Promise(resolve => {
            window.setTimeout(resolve, milliseconds);
        });
    };


    const selectedLocationExternalId =
        formData.location?.externalId ??
        formData.location?.uuid ??
        formData.location?.id ??
        null;

    const setSelectedDevicesFromMultiSelect = (
        selectedDevices: DeviceOption[] | null | undefined,
    ): void => {
        const uniqueDevices = (selectedDevices ?? []).filter(
            (device, index, devices) =>
                devices.findIndex(candidate =>
                    isSameDeviceOption(candidate, device),
                ) === index,
        );

        setFormData(prev => ({
            ...prev,
            device: uniqueDevices,
        }));
    };

    const removeDevice = (id: string) =>
        setFormData(prev => ({
            ...prev,
            device: prev.device.filter(device => device.id !== id),
        }));

    // -----------------------------------------------------------------------
    // Apply immediately / mark as done helpers
    // -----------------------------------------------------------------------

    const getSelectedDeviceIds = (payload: SavePayload): string[] => {
        return payload.device
            .map(device =>
                getValueAsString(device.id) ??
                getValueAsString(device.uuid) ??
                getValueAsString(device.externalId),
            )
            .filter(Boolean) as string[];
    };

    const getPlanogramNameForMatching = (): string => {
        return normalizeText(
            planogramData?.name ??
            planogramTitle ??
            '',
        );
    };

    const getPendingRowId = (row: PendingPlanogramRow): string | null => {
        return (
            getValueAsString(row.id) ??
            getValueAsString(row.externalId) ??
            getValueAsString(row.uuid)
        );
    };

    const getPendingRowDeviceId = (row: PendingPlanogramRow): string | null => {
        return (
            getValueAsString(row.device?.id) ??
            getValueAsString(row.device?.externalId) ??
            getValueAsString(row.device?.uuid)
        );
    };

    const isPendingRowRecentlyCreated = (
        row: PendingPlanogramRow,
        requestStartedAt: Date,
    ): boolean => {
        if (!row.createdAt) return true;

        const createdAtTime = new Date(row.createdAt).getTime();

        if (Number.isNaN(createdAtTime)) return true;

        const requestStartedAtTime = requestStartedAt.getTime();

        // Allow small clock differences between browser and backend.
        return createdAtTime >= requestStartedAtTime - 2 * 60 * 1000;
    };

    const isPendingRowStatusMarkable = (row: PendingPlanogramRow): boolean => {
        const status = String(row.status ?? '').toUpperCase();

        if (!status) return true;

        return status === 'TO_BE_DONE' || status === 'PLANNED' || status === 'PENDING';
    };

    const isMatchingPendingRow = (
        row: PendingPlanogramRow,
        payload: SavePayload,
        requestStartedAt: Date,
    ): boolean => {
        const rowId = getPendingRowId(row);

        if (!rowId) return false;

        const selectedDeviceIds = getSelectedDeviceIds(payload);
        const rowDeviceId = getPendingRowDeviceId(row);

        const deviceMatches =
            selectedDeviceIds.length === 0 ||
            selectedDeviceIds.some(deviceId => deviceId === rowDeviceId);

        if (!deviceMatches) return false;

        const expectedPlanogramName = getPlanogramNameForMatching();
        const rowPlanogramName = normalizeText(row.name);

        const nameMatches =
            !expectedPlanogramName ||
            rowPlanogramName === expectedPlanogramName;

        if (!nameMatches) return false;

        const expectedImplementationDate = getDateKey(payload.implementationDate);
        const rowImplementationDate = getDateKey(row.implementationDate);

        const implementationDateMatches =
            !expectedImplementationDate ||
            !rowImplementationDate ||
            expectedImplementationDate === rowImplementationDate;

        if (!implementationDateMatches) return false;

        if (!isPendingRowRecentlyCreated(row, requestStartedAt)) return false;

        return isPendingRowStatusMarkable(row);
    };

    const fetchCreatedPendingIdsFromPendingList = async (
        payload: SavePayload,
        requestStartedAt: Date,
    ): Promise<string[]> => {
        const response = await PlanogramPendingService.getList({
            first: 0,
            rows: 25,
            max: 25,
            sortField: 'createdAt',
            sortOrder: -1,
            showOnlyActive: false,
        });

        const rows = getArrayFromResponse<PendingPlanogramRow>(response);

        const ids = rows
            .filter(row => isMatchingPendingRow(row, payload, requestStartedAt))
            .map(getPendingRowId)
            .filter(Boolean) as string[];

        return Array.from(new Set(ids));
    };

    const fetchCreatedPendingIdsFromPendingListWithRetry = async (
        payload: SavePayload,
        requestStartedAt: Date,
    ): Promise<string[]> => {
        const retryDelays = [0, 350, 700, 1200];

        for (const delay of retryDelays) {
            if (delay > 0) {
                await sleep(delay);
            }

            const pendingIds = await fetchCreatedPendingIdsFromPendingList(
                payload,
                requestStartedAt,
            );

            if (pendingIds.length > 0) {
                return pendingIds;
            }
        }

        return [];
    };

    const markCreatedPendingsAsDone = async (
        payload: SavePayload,
        requestStartedAt: Date,
    ): Promise<void> => {
        const pendingIds = await fetchCreatedPendingIdsFromPendingListWithRetry(
            payload,
            requestStartedAt,
        );

        if (pendingIds.length === 0) {
            console.warn(
                'CopyPlanogramToDeviceDialog::applyImmediately - created pending not found on pending list, skipping markAsDone.',
            );
            return;
        }

        await Promise.all(
            pendingIds.map(pendingId =>
                PlanogramPendingService.markAsDone(pendingId),
            ),
        );
    };

    /**
     * Process every planogram-device pair independently and sequentially.
     *
     * The backend endpoint accepts an array, but one batched request does not
     * create/complete the assignment workflow independently for each selected
     * device. Sending exactly one device per call guarantees this order:
     *
     *   create pending(device A) -> mark pending(device A) as done
     *   create pending(device B) -> mark pending(device B) as done
     *
     * Sequential execution also prevents the pending-list lookup for one device
     * from racing with the lookup for another device.
     */
    const saveAndMaybeMarkAsDone = async (
        payload: SavePayload,
        shouldMarkAsDone: boolean,
    ): Promise<number> => {
        const uniqueDevices = payload.device.filter(
            (device, index, devices) =>
                devices.findIndex(candidate =>
                    isSameDeviceOption(candidate, device),
                ) === index,
        );

        for (const device of uniqueDevices) {
            const requestStartedAt = new Date();

            const payloadForDevice: SavePayload = {
                ...payload,
                device: [device],
                // Backend applyImmediately is deliberately not used.
                // First create a normal pending for this single device.
                applyImmediately: false,
            };

            await onSave(payloadForDevice);

            if (shouldMarkAsDone) {
                await markCreatedPendingsAsDone(
                    payloadForDevice,
                    requestStartedAt,
                );
            }
        }

        return uniqueDevices.length;
    };

    // -----------------------------------------------------------------------
    // Reset on open
    // -----------------------------------------------------------------------

    useEffect(() => {
        if (!visible) return;

        setStep(1);
        setEffectiveDate(null);
        setInternalLocationOptions([]);
        setInternalDeviceOptions([]);

        setFormData({
            device: defaultValues?.device ?? [],
            location: defaultValues?.location ?? null,
            applyImmediately: defaultValues?.applyImmediately ?? false,
            clearCells: defaultValues?.clearCells ?? false,
        });

        try {
            const complete = typeof isPlanogramComplete === 'function'
                ? !!isPlanogramComplete()
                : true;

            setShowIncompleteWarning(!complete);
        } catch {
            setShowIncompleteWarning(false);
        }
    }, [
        visible,
        defaultValues,
        isPlanogramComplete,
    ]);

    // -----------------------------------------------------------------------
    // Fetch locations
    // -----------------------------------------------------------------------

    useEffect(() => {
        if (!visible) return;

        const requestId = ++locationsRequestIdRef.current;

        const fetchLocations = async () => {
            if (!resolvedColumns || !resolvedRows) {
                setInternalLocationOptions([]);
                return;
            }

            setIsLoadingLocations(true);

            try {
                const response = await PlanogramPendingService.getLocationsList(
                    resolvedColumns,
                    resolvedRows,
                    deviceExternalIdToExclude,
                    planogramData ?? null,
                );

                if (locationsRequestIdRef.current !== requestId) return;

                const locations = getArrayFromResponse<Record<string, unknown>>(response)
                    .map(mapLocationToOption)
                    .filter(location => Boolean(location.id));

                setInternalLocationOptions(locations);

                setFormData(prev => {
                    if (!prev.location) return prev;

                    const locationStillAvailable = locations.some(location =>
                        location.id === prev.location?.id ||
                        location.uuid === prev.location?.uuid ||
                        location.externalId === prev.location?.externalId,
                    );

                    if (locationStillAvailable) return prev;

                    return {
                        ...prev,
                        location: null,
                        device: [],
                    };
                });
            } catch (error) {
                console.error('CopyPlanogramToDeviceDialog::fetchLocations-error', error);

                if (locationsRequestIdRef.current !== requestId) return;

                setInternalLocationOptions([]);
            } finally {
                if (locationsRequestIdRef.current === requestId) {
                    setIsLoadingLocations(false);
                }
            }
        };

        void fetchLocations();
    }, [
        visible,
        resolvedColumns,
        resolvedRows,
        deviceExternalIdToExclude,
        planogramData,
    ]);

    // -----------------------------------------------------------------------
    // Fetch devices
    // -----------------------------------------------------------------------

    useEffect(() => {
        if (!visible) return;

        const requestId = ++devicesRequestIdRef.current;

        const fetchDevices = async () => {
            if (!resolvedColumns || !resolvedRows) {
                setInternalDeviceOptions([]);
                setFormData(prev => ({
                    ...prev,
                    device: [],
                }));
                return;
            }

            setIsLoadingDevices(true);

            try {
                // Location is optional:
                // - when selectedLocationExternalId is null, fetch all devices that fit this planogram size
                // - when selectedLocationExternalId is set, fetch only devices valid for that location
                const response = await PlanogramPendingService.getDevicesList(
                    resolvedColumns,
                    resolvedRows,
                    selectedLocationExternalId,
                    planogramExternalIdToExclude,
                    deviceExternalIdToExclude,
                    planogramData ?? null,
                );

                if (devicesRequestIdRef.current !== requestId) return;

                const devices = getArrayFromResponse<Record<string, unknown>>(response)
                    .map(mapDeviceToOption)
                    .filter(device => Boolean(device.id));

                setInternalDeviceOptions(devices);

                setFormData(prev => ({
                    ...prev,
                    device: prev.device.filter(selectedDevice =>
                        devices.some(device =>
                            isSameDeviceOption(device, selectedDevice),
                        ),
                    ),
                }));
            } catch (error) {
                console.error('CopyPlanogramToDeviceDialog::fetchDevices-error', error);

                if (devicesRequestIdRef.current !== requestId) return;

                setInternalDeviceOptions([]);
                setFormData(prev => ({
                    ...prev,
                    device: [],
                }));
            } finally {
                if (devicesRequestIdRef.current === requestId) {
                    setIsLoadingDevices(false);
                }
            }
        };

        void fetchDevices();
    }, [
        visible,
        resolvedColumns,
        resolvedRows,
        selectedLocationExternalId,
        planogramExternalIdToExclude,
        deviceExternalIdToExclude,
        planogramData,
    ]);

    // -----------------------------------------------------------------------
    // Submit
    // -----------------------------------------------------------------------

    const handleSubmit = async (): Promise<void> => {
        if (formData.device.length === 0) return;

        if (step === 4) {
            setIsSaving(true);

            try {
                const processedDeviceCount = await saveAndMaybeMarkAsDone(
                    {
                        ...formData,
                        allDevicesAtLocation: false,
                        implementationDate: effectiveDate
                            ? new Date(effectiveDate).toISOString()
                            : new Date().toISOString(),
                    },
                    formData.applyImmediately,
                );

                onComplete?.(processedDeviceCount);
                onHide();
            } finally {
                setIsSaving(false);
            }

            return;
        }

        if (step === 1) {
            if (formData.applyImmediately) {
                const assignmentDate = new Date();

                setIsSaving(true);

                try {
                    const processedDeviceCount = await saveAndMaybeMarkAsDone(
                        {
                            ...formData,
                            allDevicesAtLocation: false,
                            implementationDate: assignmentDate.toISOString(),
                        },
                        true,
                    );

                    onComplete?.(processedDeviceCount);
                    onHide();
                } finally {
                    setIsSaving(false);
                }

                return;
            }

            if (showIncompleteWarning) {
                setStep(2);
                return;
            }

            const nextBusinessDay = getNextBusinessDay(new Date());
            setEffectiveDate(nextBusinessDay);
            setStep(4);
            return;
        }

        if (step === 2) {
            const nextBusinessDay = getNextBusinessDay(new Date());
            setEffectiveDate(nextBusinessDay);
            setStep(4);
            return;
        }

        if (step === 3) {
            if (!effectiveDate) return;
            setStep(4);
        }
    };

    // -----------------------------------------------------------------------
    // Header
    // -----------------------------------------------------------------------

    const dialogHeader = useMemo(() => {
        if (showIncompleteWarning && step <= 2) {
            return incompleteTitle ?? t('planogram.copyToDevice.incompleteTitle');
        }

        return title ?? t('planogram.copyToDevice.title');
    }, [
        showIncompleteWarning,
        step,
        incompleteTitle,
        title,
        t,
    ]);

    // -----------------------------------------------------------------------
    // Small render helpers
    // -----------------------------------------------------------------------

    const renderIncompleteWarningTitle = () => {
        const warningTitle = t('planogram.copyToDevice.incompleteWarningTitle');
        const colonIndex = warningTitle.indexOf(':');

        if (colonIndex === -1) {
            return <span className="font-semibold">{warningTitle}</span>;
        }

        return (
            <>
                <span className="font-semibold">
                    {warningTitle.slice(0, colonIndex + 1)}
                </span>
                <span className="ml-1">
                    {warningTitle.slice(colonIndex + 1).trim()}
                </span>
            </>
        );
    };

    const DeviceCountBadge = () => (
        <div className="flex align-items-center gap-1">
            <span className="p-multiselect-selected-count-label">
                {t('multiselect.selected', 'Selected:')}
            </span>
            <span className="p-multiselect-selected-count-value">
                <span className="p-multiselect-selected-count-value-text">
                    {formData.device.length}
                </span>
            </span>
        </div>
    );

    const PlanogramInfoField = () => (
        <div className="flex flex-column gap-2 w-full">
            <label className="field-label">
                {t('planogram.copyToDevice.planogramLabel')}
            </label>
            <div
                className="planogram-info p-inputtext p-component w-full"
                style={{ cursor: 'not-allowed' }}
            >
                <span className="planogram-title-text mr-1">
                    {planogramTitle
                        ? planogramTitle + (resolvedRows && resolvedColumns ? ' /' : '')
                        : ''}
                </span>
                <span className="planogram-dimensions-text">
                    {resolvedRows && resolvedColumns
                        ? ` ${resolvedRows} × ${resolvedColumns}`
                        : ''}
                </span>
            </div>
        </div>
    );

    const DevicesList = () => (
        <div className="flex flex-column gap-2 w-full">
            <div className="flex justify-content-between align-items-center">
                <label className="field-label">
                    {t('planogram.copyToDevice.devicesLabel')}
                </label>
                <DeviceCountBadge />
            </div>

            <div
                className="devices-list flex flex-column"
                style={
                    formData.device.length > 3
                        ? { maxHeight: '12rem', overflowY: 'auto' }
                        : undefined
                }
            >
                {formData.device.map((device, index) => (
                    <div
                        key={device.id ?? index}
                        className="devices-list-item flex justify-content-between align-items-center"
                    >
                        <span className="text-sm">{device.label}</span>
                        <i
                            className="pi pi-times-circle text-red-500 cursor-pointer"
                            onClick={() => removeDevice(device.id)}
                        />
                    </div>
                ))}
            </div>
        </div>
    );

    // -----------------------------------------------------------------------
    // Footer
    // -----------------------------------------------------------------------

    const hasDevice = formData.device.length > 0;
    const canProceed = hasDevice && !isSaving;

    const footer = (
        <div className="flex w-full gap-3">
            {step === 1 && (
                <>
                    {!hasDevice && (
                        <>
                            <Button
                                label={t('common.save')}
                                disabled
                                className="flex-1"
                            />
                            <Button
                                label={t('common.cancel')}
                                onClick={onHide}
                                className="flex-1 p-button-secondary"
                            />
                        </>
                    )}

                    {hasDevice && formData.applyImmediately && (
                        <>
                            <Button
                                ref={saveButtonRef}
                                label={t('common.save')}
                                onClick={() => void handleSubmit()}
                                disabled={isSaving}
                                loading={isSaving}
                                className="flex-1"
                            />
                            <Button
                                label={t('common.cancel')}
                                onClick={onHide}
                                className="flex-1 p-button-secondary"
                                disabled={isSaving}
                            />
                        </>
                    )}

                    {hasDevice && !formData.applyImmediately && (
                        <>
                            <Button
                                label={t('planogram.copyToDevice.nextBusinessDay')}
                                onClick={() => void handleSubmit()}
                                disabled={isSaving}
                                loading={isSaving}
                                className="flex-1"
                            />
                            <Button
                                label={t('planogram.copyToDevice.selectedDay')}
                                onClick={() => setStep(3)}
                                disabled={isSaving}
                                className="flex-1 p-button-secondary"
                            />
                        </>
                    )}
                </>
            )}

            {step === 2 && (
                <>
                    <Button
                        label={t('planogram.copyToDevice.nextBusinessDay')}
                        onClick={() => void handleSubmit()}
                        disabled={!canProceed}
                        loading={isSaving}
                        className="flex-1"
                    />
                    <Button
                        label={t('planogram.copyToDevice.selectedDay')}
                        onClick={() => setStep(3)}
                        className="flex-1 p-button-secondary"
                    />
                </>
            )}

            {step === 3 && (
                <>
                    <Button
                        label={t('common.save')}
                        onClick={() => void handleSubmit()}
                        disabled={isSaving || !effectiveDate}
                        loading={isSaving}
                        className="flex-1"
                    />
                    <Button
                        label={t('common.cancel')}
                        onClick={onHide}
                        className="flex-1 p-button-secondary"
                        disabled={isSaving}
                    />
                </>
            )}

            {step === 4 && (
                <>
                    <Button
                        ref={saveButtonRef}
                        label={t('common.save')}
                        onClick={() => void handleSubmit()}
                        disabled={isSaving}
                        loading={isSaving}
                        className="flex-1"
                    />
                    <Button
                        label={t('common.cancel')}
                        onClick={onHide}
                        className="flex-1 p-button-secondary"
                        disabled={isSaving}
                    />
                </>
            )}
        </div>
    );

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={dialogHeader}
            className="copy-planogram-to-device-dialog"
            closable
            closeOnEscape
            modal
            draggable={false}
            resizable={false}
            focusOnShow
            footer={footer}
        >
            {step === 1 && (
                <div className="flex flex-column gap-3">
                    {(planogramTitle || (resolvedRows && resolvedColumns)) && (
                        <div className="planogram-subtitle">
                            {planogramTitle ?? ''}
                            {resolvedRows && resolvedColumns
                                ? ` ${resolvedRows} × ${resolvedColumns}`
                                : ''}
                        </div>
                    )}

                    <div className="text-color-secondary text-sm line-height-3 m-0 description">
                        {t('planogram.copyToDevice.description')}
                    </div>

                    <div className="flex flex-column gap-2 w-full">
                        <label className="field-label">
                            {t('planogram.copyToDevice.location')}
                        </label>
                        <Dropdown
                            id="location"
                            dataKey="id"
                            optionLabel="label"
                            value={formData.location}
                            options={internalLocationOptions}
                            onChange={(event: { value: LocationOption | null }) =>
                                setFormData(prev => ({
                                    ...prev,
                                    location: event.value,
                                    device: [],
                                }))
                            }
                            placeholder={t('common.select')}
                            className="w-full"
                            loading={isLoadingLocations}
                            showClear
                            filter
                            label={null}
                            validator={null}
                            validations={null}
                        />
                    </div>

                    <div className="flex flex-column gap-2 w-full">
                        <div className="flex justify-content-between align-items-center">
                            <label className="field-label">
                                {t('planogram.copyToDevice.device')}
                            </label>
                            <DeviceCountBadge />
                        </div>
                        <MultiSelect
                            id="device"
                            dataKey="id"
                            optionLabel="label"
                            value={formData.device}
                            options={internalDeviceOptions}
                            onChange={(event: { value: DeviceOption[] | null }) =>
                                setSelectedDevicesFromMultiSelect(event.value)
                            }
                            placeholder={t('common.select')}
                            className="w-full"
                            loading={isLoadingDevices}
                            showSelectAll={false}
                            showClear={false}
                            maxSelectedItemsVisible={3}
                            filter
                            filterFunction={(options: DeviceOption[], search: string) =>
                                options.filter(option =>
                                    option.label?.toLowerCase().includes(search.toLowerCase()),
                                )
                            }
                            searchPlaceholder={t('common.search')}
                            showLabel={false}
                            label={null}
                            validator={null}
                            validations={null}
                        />
                    </div>

                    <div className="flex align-items-center gap-2 w-fit">
                        <InputSwitch
                            id="apply-immediately"
                            label={t('planogram.applyImmediately.label')}
                            checked={formData.applyImmediately}
                            onChange={(event: { value: boolean }) =>
                                setFormData(prev => ({
                                    ...prev,
                                    applyImmediately: event.value,
                                }))
                            }
                            className="w-auto"
                        />
                        <QuestionMarkIcon className="apply-immediately-tip" />
                        <Tooltip target=".apply-immediately-tip" position="right">
                            {t('planogram.applyImmediately.tooltip')}
                        </Tooltip>
                    </div>
                </div>
            )}

            {step === 2 && (
                <div className="flex flex-column gap-4">
                    <div>
                        <div className="text-orange-500 mb-2 flex align-items-center gap-2">
                            <WarningTriangleIcon />
                            {renderIncompleteWarningTitle()}
                        </div>
                        <div className="text-color-secondary text-sm line-height-3 m-0">
                            {t('planogram.copyToDevice.incompleteWarningDescription')}
                        </div>
                    </div>

                    <PlanogramInfoField />
                    <DevicesList />
                </div>
            )}

            {step === 3 && (
                <div className="flex flex-column gap-3">
                    {(planogramTitle || (resolvedRows && resolvedColumns)) && (
                        <div className="planogram-subtitle">
                            {planogramTitle ?? ''}
                            {resolvedRows && resolvedColumns
                                ? ` ${resolvedRows} × ${resolvedColumns}`
                                : ''}
                        </div>
                    )}

                    <div className="text-color-secondary text-sm line-height-3 m-0 description">
                        {t('planogram.copyToDevice.step3Description')}
                    </div>

                    <div className="flex justify-content-between align-items-center">
                        <label className="field-label mb-0">
                            {t('planogram.copyToDevice.devicesLabel')}
                        </label>
                        <DeviceCountBadge />
                    </div>

                    <div className="flex flex-column gap-2 w-full">
                        <label className="field-label">
                            {t('planogram.copyToDevice.date')}
                        </label>
                        <CalendarInput
                            id="copy-planogram-implementation-date"
                            value={effectiveDate}
                            onChange={(event: { value: Date | null }) =>
                                setEffectiveDate(event.value ?? null)
                            }
                            dateFormat="yy-mm-dd"
                            showTime
                            hourFormat="24"
                            className="planogram-calendar"
                            inputClassName="planogram-calendar__input"
                            panelClassName="copy-planogram-calendar-panel"
                            showLabel={false}
                            iconPosition="left"
                            placeholder={t('common.select')}
                        />
                    </div>
                </div>
            )}

            {step === 4 && (
                <div className="flex flex-column gap-3">
                    {(planogramTitle || (resolvedRows && resolvedColumns)) && (
                        <div className="planogram-subtitle">
                            {planogramTitle ?? ''}
                            {resolvedRows && resolvedColumns
                                ? ` ${resolvedRows} × ${resolvedColumns}`
                                : ''}
                        </div>
                    )}

                    <div className="text-color-secondary text-sm line-height-3 m-0 description">
                        {t('planogram.copyToDevice.confirmationDescription')}
                    </div>
                </div>
            )}
        </Dialog>
    );
};

export default CopyPlanogramToDeviceDialog;